import type { PodcastMediaKit } from "../../../api/podcasts";

export type YoutubeConsumptionSlice = {
  totalViews?: number;
  totalInteractions?: number;
  contentCount?: number;
  avgViewsPerContent?: number;
  avgInteractions?: number;
};

export type PlatformMetrics = {
  views: number | null;
  contentCount: number | null;
  avgViews: number | null;
  engagementPercent: number | null;
};

export type PodscopeCardProps = {
  promEspectadores: number | null;
  peakEspectadores: number | null;
  promMensajes: number | null;
  promEspChat: number | null;
  streamCount: number | null;
};

export type ParsedPlatformMetrics = {
  youtubeLong: PlatformMetrics;
  youtubeShorts: PlatformMetrics;
  instagram: PlatformMetrics;
  facebook: PlatformMetrics;
  tiktok: PlatformMetrics;
  podscopeCard: PodscopeCardProps;
  snapshotStreamingActive: boolean;
  hasAnyPlatformCell: boolean;
};

export function fmtMetric(n: number | undefined | null): string {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Math.round(Number(n)).toLocaleString("es-CL");
}

export function fmtEngagement(p: number | null | undefined): string {
  if (p == null || Number.isNaN(Number(p))) return "—";
  return `${Number(p).toFixed(1)}%`;
}

function estContentCount(
  totalViews: number,
  avgViewsPerContent: number | undefined,
): number | null {
  if (!totalViews || !avgViewsPerContent || avgViewsPerContent <= 0) {
    return null;
  }
  return Math.max(1, Math.round(totalViews / avgViewsPerContent));
}

function contentCountFromApiOrEstimate(
  consumption:
    | {
        contentCount?: number;
        avgViewsPerContent?: number;
      }
    | undefined,
  totalViews: number,
): number | null {
  const direct = consumption?.contentCount;
  if (typeof direct === "number" && Number.isFinite(direct) && direct > 0) {
    return Math.round(direct);
  }
  return estContentCount(totalViews, consumption?.avgViewsPerContent);
}

export function buildYoutubeSliceMetrics(
  consumption: YoutubeConsumptionSlice | undefined,
  engagement?: { engagementRate?: number } | null,
): PlatformMetrics {
  const empty: PlatformMetrics = {
    views: null,
    contentCount: null,
    avgViews: null,
    engagementPercent: null,
  };
  if (
    !consumption ||
    !Object.values(consumption).some((v) => v != null)
  ) {
    return empty;
  }

  const v = Number(consumption.totalViews ?? 0);
  const i = Number(consumption.totalInteractions ?? 0);
  const contentCount = contentCountFromApiOrEstimate(consumption, v);

  let avgViews: number | null = null;
  if (v > 0 && contentCount && contentCount > 0) {
    avgViews = Math.round((v / contentCount) * 10) / 10;
  } else if (consumption.avgViewsPerContent != null) {
    avgViews = consumption.avgViewsPerContent;
  }

  let engagementPercent: number | null = null;
  if (v > 0 && i > 0) {
    engagementPercent = Math.round((i / v) * 10000) / 100;
  } else if (engagement?.engagementRate != null) {
    engagementPercent = Number(engagement.engagementRate);
  }

  return {
    views: v > 0 ? v : null,
    contentCount,
    avgViews,
    engagementPercent,
  };
}

export function buildInstagramMetrics(
  ig: NonNullable<PodcastMediaKit["analytics"]>["instagram"],
): PlatformMetrics {
  const empty: PlatformMetrics = {
    views: null,
    contentCount: null,
    avgViews: null,
    engagementPercent: null,
  };
  if (!ig || Object.keys(ig).length === 0) return empty;

  const r = ig.reach;
  const v = r?.totalViews != null ? Number(r.totalViews) : 0;
  const interactions =
    r?.totalInteractions != null ? Number(r.totalInteractions) : 0;

  let contentCount: number | null = null;
  if (r?.contentCount != null && Number(r.contentCount) > 0) {
    contentCount = Math.round(Number(r.contentCount));
  } else if (v > 0 && r?.avgViews != null && r.avgViews > 0) {
    contentCount = Math.max(1, Math.round(v / r.avgViews));
  }

  let avgViews: number | null = null;
  if (v > 0 && contentCount && contentCount > 0) {
    avgViews = Math.round((v / contentCount) * 10) / 10;
  } else if (r?.avgViews != null && r.avgViews > 0) {
    avgViews = r.avgViews;
  }

  let engagementPercent: number | null = null;
  if (ig.engagement?.engagementRate != null) {
    engagementPercent = Number(ig.engagement.engagementRate);
  } else if (v > 0 && interactions > 0) {
    engagementPercent = Math.round((interactions / v) * 10000) / 100;
  }

  return {
    views: v > 0 ? v : null,
    contentCount,
    avgViews,
    engagementPercent,
  };
}

export function buildTiktokMetrics(
  tt: NonNullable<PodcastMediaKit["analytics"]>["tiktok"],
): PlatformMetrics {
  const empty: PlatformMetrics = {
    views: null,
    contentCount: null,
    avgViews: null,
    engagementPercent: null,
  };
  if (!tt || Object.keys(tt).length === 0) return empty;

  const c = tt.consumption;
  const v = c?.totalViews != null ? Number(c.totalViews) : 0;
  const interactions =
    c?.totalInteractions != null ? Number(c.totalInteractions) : 0;

  let contentCount: number | null = null;
  if (c?.contentCount != null && Number(c.contentCount) > 0) {
    contentCount = Math.round(Number(c.contentCount));
  } else {
    contentCount = estContentCount(v, c?.avgViewsPerContent);
  }

  let avgViews: number | null = null;
  if (v > 0 && contentCount && contentCount > 0) {
    avgViews = Math.round((v / contentCount) * 10) / 10;
  } else if (c?.avgViewsPerContent != null) {
    avgViews = c.avgViewsPerContent;
  }

  let engagementPercent: number | null = null;
  if (tt.engagement?.engagementRate != null) {
    engagementPercent = Number(tt.engagement.engagementRate);
  } else if (v > 0 && interactions > 0) {
    engagementPercent = Math.round((interactions / v) * 10000) / 100;
  }

  return {
    views: v > 0 ? v : null,
    contentCount,
    avgViews,
    engagementPercent,
  };
}

export function buildFacebookMetrics(
  fb: NonNullable<PodcastMediaKit["analytics"]>["facebook"],
): PlatformMetrics {
  const empty: PlatformMetrics = {
    views: null,
    contentCount: null,
    avgViews: null,
    engagementPercent: null,
  };
  if (!fb || Object.keys(fb).length === 0) return empty;

  const c = fb.consumption;
  const v = c?.totalViews != null ? Number(c.totalViews) : 0;
  const interactions =
    c?.totalInteractions != null ? Number(c.totalInteractions) : 0;

  let contentCount: number | null = null;
  if (c?.contentCount != null && Number(c.contentCount) > 0) {
    contentCount = Math.round(Number(c.contentCount));
  } else {
    contentCount = estContentCount(v, c?.avgViewsPerContent);
  }

  let avgViews: number | null = null;
  if (v > 0 && contentCount && contentCount > 0) {
    avgViews = Math.round((v / contentCount) * 10) / 10;
  } else if (c?.avgViewsPerContent != null) {
    avgViews = c.avgViewsPerContent;
  }

  let engagementPercent: number | null = null;
  if (fb.engagement?.engagementRate != null) {
    engagementPercent = Number(fb.engagement.engagementRate);
  } else if (v > 0 && interactions > 0) {
    engagementPercent = Math.round((interactions / v) * 10000) / 100;
  }

  return {
    views: v > 0 ? v : null,
    contentCount,
    avgViews,
    engagementPercent,
  };
}

export function hasMetricValues(m: PlatformMetrics): boolean {
  return (
    m.views != null ||
    m.contentCount != null ||
    m.avgViews != null ||
    m.engagementPercent != null
  );
}

export function parsePlatformMetrics(
  analytics: PodcastMediaKit["analytics"] | undefined,
): ParsedPlatformMetrics {
  const yt = analytics?.youtube;
  const ig = analytics?.instagram;
  const fb = analytics?.facebook;
  const tt = analytics?.tiktok;
  const ps = analytics?.podscope;

  const youtubeLong = buildYoutubeSliceMetrics(
    yt?.longForm?.consumption ?? yt?.consumption,
    yt?.longForm?.engagement ?? yt?.engagement,
  );
  const youtubeShorts = buildYoutubeSliceMetrics(
    yt?.shorts?.consumption,
    yt?.shorts?.engagement,
  );
  const instagram = buildInstagramMetrics(ig);
  const facebook = buildFacebookMetrics(fb);
  const tiktok = buildTiktokMetrics(tt);

  const streamingFromSnapshots = ps?.streamingFromSnapshots;

  const snapshotStreamingActive =
    streamingFromSnapshots != null &&
    (streamingFromSnapshots.streamCount ?? 0) > 0;

  const podscopeCard: PodscopeCardProps = {
    promEspectadores: snapshotStreamingActive
      ? (streamingFromSnapshots!.avgViewers ?? null)
      : null,
    peakEspectadores: snapshotStreamingActive
      ? (streamingFromSnapshots!.peakViewers ?? null)
      : null,
    promMensajes: snapshotStreamingActive
      ? (streamingFromSnapshots!.avgChatMessages ?? null)
      : null,
    promEspChat: snapshotStreamingActive
      ? (streamingFromSnapshots!.avgChatUniqueUsers ?? null)
      : null,
    streamCount: snapshotStreamingActive
      ? (streamingFromSnapshots!.streamCount ?? null)
      : null,
  };

  const hasAnyPlatformCell =
    hasMetricValues(youtubeLong) ||
    hasMetricValues(youtubeShorts) ||
    hasMetricValues(instagram) ||
    hasMetricValues(facebook) ||
    hasMetricValues(tiktok);

  return {
    youtubeLong,
    youtubeShorts,
    instagram,
    facebook,
    tiktok,
    podscopeCard,
    snapshotStreamingActive,
    hasAnyPlatformCell:
      hasAnyPlatformCell ||
      snapshotStreamingActive ||
      hasLegacyPodscopeStreaming(ps) ||
      ps?.programRank != null,
  };
}

function hasLegacyPodscopeStreaming(
  ps: NonNullable<PodcastMediaKit["analytics"]>["podscope"] | undefined,
): boolean {
  if (!ps?.streaming) return false;
  const s = ps.streaming;
  return (
    (s.avgViewers ?? 0) > 0 ||
    (s.peakViewers ?? 0) > 0 ||
    (s.chatMessages ?? 0) > 0 ||
    (s.chatMessagesAvg ?? 0) > 0 ||
    (s.uniqueChatUsersAvg ?? 0) > 0
  );
}
